import { useQuery } from '@apollo/client';
import { GridEventListener, useGridApiRef } from '@mui/x-data-grid-premium';
import { useEffect, useState } from 'react';

import GET_DEFENDANTS_PAGED from '../../../../api/apollo/query/GetDefendantsPages';
import { useAuth } from '../../../../api/auth';
import { CustomDataGrid } from '../../../../components/CustomDataGrid';
import TableToolbar from '../../../../components/Table/TableToolbar';
import { DefaultTableProps } from '../../../../components/Table/types';
import demoDefendantData from '../../../../static/data/demo_defendants_data.json';
import { useSummaryTableData } from '../../SummaryTableDataContext';
import columns from './columns';

const Defendants = ({ shrinkTitle, hideHeaders, showHeaders }: DefaultTableProps) => {
    const { user } = useAuth();
    const apiRef = useGridApiRef();
    const { tableData, setTableRows, appendTableRows } = useSummaryTableData();
    const [isFetchingAllPages, setIsFetchingAllPages] = useState(false);
    const rows = tableData['defendants'] ?? [];

    const PAGE_SIZE = 1000;

    const { loading, fetchMore } = useQuery(GET_DEFENDANTS_PAGED, {
        variables: { first: PAGE_SIZE },
        skip: tableData['defendants'] != null,
        onCompleted: ({ defendantsPaged }) => {
            const nodes = defendantsPaged.edges.map(({ node }) => node);
            setTableRows('defendants', nodes);

            const fetchAllPages = async (hasNextPage: boolean, endCursor: string) => {
                if (!hasNextPage) return;
                const { data } = await fetchMore({
                    variables: { first: PAGE_SIZE, after: endCursor },
                });
                const moreNodes = data.defendantsPaged.edges.map(({ node }) => node);
                appendTableRows('defendants', moreNodes);
                await fetchAllPages(data.defendantsPaged.pageInfo.hasNextPage, data.defendantsPaged.pageInfo.endCursor);
            };

            if (defendantsPaged.pageInfo.hasNextPage) {
                setIsFetchingAllPages(true);
                fetchAllPages(defendantsPaged.pageInfo.hasNextPage, defendantsPaged.pageInfo.endCursor)
                    .finally(() => setIsFetchingAllPages(false));
            }
        },
        onError: () => setTableRows('defendants', demoDefendantData),
    });

    useEffect(() => {
        const handleEvent: GridEventListener<'rowsScroll'> = (params) => {
            params.top > 5 ? hideHeaders() : showHeaders();
        };

        apiRef.current.subscribeEvent('rowsScroll', handleEvent);
    }, [apiRef, hideHeaders, showHeaders]);

    return (
        <CustomDataGrid
            apiRef={apiRef}
            columns={columns}
            rows={rows}
            checkboxSelection={false}
            loading={loading || isFetchingAllPages}
            hideFooterSelectedRowCount
            components={{ Toolbar: TableToolbar }}
            componentsProps={{
                toolbar: {
                    title: 'Defendants',
                    showTitle: shrinkTitle,
                    csvExportEnabled:
                        !loading && !isFetchingAllPages && user?.permissions?.map(({ codename }) => codename).includes('can_export_tables'),
                    tableApi: apiRef.current,
                    excelExportEnabled: false,
                },
            }}
        />
    );
};

export default Defendants;
